import React, { useState, useEffect } from 'react';
import Button from './Button';

const AddQuestionModal = ({ isOpen, onClose, onAddQuestion, sessionId }) => {
  const [questionContent, setQuestionContent] = useState('');
  const [submitterName, setSubmitterName] = useState('');
  const [creating, setCreating] = useState(false);

  // Reset form when modal opens/closes
  useEffect(() => {
    if (isOpen) {
      setQuestionContent('');
      setSubmitterName('');
    }
  }, [isOpen]);

  const handleAdd = async () => {
    if (!questionContent.trim()) return;
    
    setCreating(true);
    try {
      const questionData = { 
        content: questionContent.trim(),
        submitter_name: submitterName.trim() || 'Anonymous',
        sessionId
      };
      
      await onAddQuestion(questionData);
      setQuestionContent('');
      setSubmitterName('');
      onClose();
    } finally {
      setCreating(false);
    }
  };

  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
      <div className="bg-white rounded-lg shadow-xl max-w-md w-full">
        <div className="p-6">
          {/* Header */}
          <div className="flex justify-between items-start mb-6">
            <div>
              <h2 className="text-xl font-bold text-gray-900">
                Add New Question
              </h2>
              <p className="text-gray-600 text-sm mt-1">
                Add a question to your session
              </p>
            </div>
            <button
              onClick={onClose}
              className="text-gray-400 hover:text-gray-600 focus:outline-none p-2 rounded-full hover:bg-gray-100"
              aria-label="Close"
            >
              <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M6 18L18 6M6 6l12 12" />
              </svg>
            </button>
          </div>

          {/* Form */}
          <div className="space-y-4">
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Question Content *
              </label>
              <textarea
                value={questionContent}
                onChange={(e) => setQuestionContent(e.target.value)}
                placeholder="Enter your question here..."
                className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500 resize-none"
                rows={4}
                autoFocus
              />
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Submitter Name (Optional)
              </label>
              <input
                type="text"
                value={submitterName}
                onChange={(e) => setSubmitterName(e.target.value)}
                placeholder="Enter your name or leave blank for anonymous"
                className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
                onKeyPress={(e) => e.key === 'Enter' && handleAdd()}
              />
            </div>

            {/* Preview */}
            <div className="pt-4 border-t border-gray-200">
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Preview
              </label>
              <div className="p-4 rounded-lg border-2 border-gray-200 bg-gray-50">
                <div className="flex items-center gap-4 mb-2">
                  <div className="text-sm font-medium text-gray-500">
                    0 VOTES
                  </div>
                  <div className="text-sm text-gray-500">
                    {submitterName.trim() || 'Anonymous'}
                  </div>
                </div>
                <p className="text-gray-900 break-words whitespace-pre-wrap">
                  {questionContent || 'Question content will appear here...'}
                </p>
              </div>
            </div>

            {/* Actions */}
            <div className="flex justify-end space-x-2 pt-4">
              <Button variant="outline" onClick={onClose} size="sm">
                Cancel
              </Button>
              <Button 
                onClick={handleAdd} 
                disabled={!questionContent.trim() || creating}
                size="sm"
              >
                {creating ? 'Adding...' : 'Add Question'}
              </Button>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default AddQuestionModal;
